import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/useAuth.js";
import { invoicesApi } from "../../api/auctiongate.js";

export default function Invoices() {
  const { user } = useAuth();
  const userId = user?.companyId;
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [createOk, setCreateOk] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await invoicesApi.list({ userId, page });
      const arr = extract(r);
      setItems(arr);
      setTotal(r?.total ?? r?.data?.total ?? null);
    } catch (e) {
      if (e?.status && e.status >= 400 && e.status < 500) {
        setItems([]);
      } else {
        setError(e?.message || "Failed to load invoices");
      }
    } finally {
      setLoading(false);
    }
  }, [userId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const body = Object.fromEntries(form.entries());
    body.user_id = userId;
    body.buyer = body.buyer || "";
    setCreating(true);
    setCreateErr(null);
    setCreateOk(null);
    try {
      const r = await invoicesApi.create(body);
      if (r?.success === false || r?.error) setCreateErr(r?.message || r?.error || "Failed");
      else setCreateOk("Invoice created.");
      await load();
    } catch (err) {
      setCreateErr(err?.message || "Failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card">
      <div className="card__title">
        Invoices
        <button className="btn btn-ghost" onClick={load} disabled={loading} style={{ fontSize: 12, padding: "6px 12px" }}>
          {loading ? <span className="loader" /> : "Refresh"}
        </button>
      </div>
      {!userId && <div className="muted small">Log in first to fetch invoices.</div>}
      {error && <div className="login-error">{error}</div>}
      {userId && !loading && !error && items.length === 0 && (
        <div className="placeholder-panel">No invoices on this page.</div>
      )}
      {items.length > 0 && (
        <div className="small">
          {items.slice(0, 15).map((it, i) => (
            <div key={it.id ?? i} style={rowStyle}>
              <span style={{ fontWeight: 600 }}>#{it.id ?? it.invoice_id ?? i + 1}</span>
              <span>{it.lot_number ?? "—"}</span>
              <span className="muted">{it.company ?? it.auction ?? ""}</span>
              <span>{it.client_name ?? "—"}</span>
              <span style={{ fontWeight: 600, textAlign: "right" }}>
                {it.sum ? `$${it.sum}` : "-"}
              </span>
            </div>
          ))}
        </div>
      )}
      {items.length > 0 && (
        <div className="row" style={{ justifyContent: "center", marginTop: 10 }}>
          <button className="btn btn-ghost" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ←
          </button>
          <span className="small muted">Page {page}</span>
          <button className="btn btn-ghost" disabled={loading} onClick={() => setPage((p) => p + 1)}>
            →
          </button>
        </div>
      )}

      <details style={{ marginTop: 14 }}>
        <summary className="small muted" style={{ cursor: "pointer" }}>Create invoice (form)</summary>
        <form onSubmit={create} className="stack" style={{ marginTop: 10 }}>
          <input name="auction" placeholder="Auction (copart / iaai / …)" required style={input} />
          <input name="lot_number" placeholder="Lot number" required style={input} />
          <input name="vin" placeholder="VIN" style={input} />
          <input name="make_model_year" placeholder="Make Model Year" style={input} />
          <input name="client_name" placeholder="Client name" required style={input} />
          <input name="passport" placeholder="Passport" style={input} />
          <input name="buyer" placeholder="Buyer number" style={input} />
          <input name="sum" placeholder="Sum" type="number" required style={input} />
          <label className="small muted"><input type="checkbox" name="by_mail" /> Send by mail</label>
          <input name="custom_email" placeholder="Custom email (optional)" style={input} />
          <button className="btn btn-primary" type="submit" disabled={creating || !userId}>
            {creating ? <span className="loader" /> : "Create"}
          </button>
          {createErr && <div className="login-error">{createErr}</div>}
          {createOk && <div className="small" style={{ color: "var(--color-success)" }}>{createOk}</div>}
        </form>
      </details>
    </div>
  );
}

function extract(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  if (Array.isArray(r.data)) return r.data;
  if (Array.isArray(r?.data?.data)) return r.data.data;
  return [];
}

const rowStyle = {
  display: "grid",
  gridTemplateColumns: ".8fr 1fr 1fr 1fr .6fr",
  gap: 8,
  padding: "6px 8px",
  borderBottom: "1px solid var(--color-border)",
};
const input = {
  padding: "7px 10px",
  border: "1px solid var(--color-border-input)",
  borderRadius: 8,
  fontSize: 13,
};
